/* eslint require-jsdoc: 'error', valid-jsdoc: ["error", { "requireReturn": false }] */
import CenterLabelExtension from './center-label-extension';
import { componentFactory } from '../../_internal/lib/lib';
import { priorityList } from '../../_internal/schedular';
/**
 * Checks if component is of type chart
 * @param {Object} component - Checks if paramter is of type chart
 * @return {boolean} Returns if component is of type chart
 */
let isDoughnut2D = component => component.getName() === 'Doughnut2D',
  isMultiLevelPie = component => component.getName() === 'MultilevelPie',
  isChartAPI = component => component.getType() === 'chartAPI',
  isDataset = component => component.getType() === 'dataset',
  count = 0,
  UNDEF;

/**
 * Sets the center label in Dougnut 2D chart. The label cosmetics are configurable via the second
 * optional parameter, which accepts a host of related properties.
 *
 * > Available on `doughnut` chart only.
 *
 * @group chart:pie-center-label
 *
 * @param {string} labelText - The text to be displayed at doughnut center.
 * @param {Object} [options] - The optional parameter that holds a host of configurable params
 * with most them being cosmetic properties of the center label. The properties are case sensitive.
 *
 * {string} [options.font] - Sets the font face of the label.
 * {string} [options.fontSize] - Defines the font size of the label.
 * {boolean} [options.bold] - Specifies of whether the label be bold.
 * {boolean} [options.italic] - Specifies of whether the label be in italic.
 * {hexcolor} [options.color] - Sets the color of the label text.
 * {alpha} [options.alpha] - Sets the opacity of the label text.
 * {hexcolor} [options.hoverColor] - Sets the hover color of the label text.
 * {alpha} [options.hoverAlpha] - Sets the hover opacity of the label text.
 * {hexcolor} [options.bgColor] - Sets the color of the label background.
 * {alpha} [options.bgAlpha] - Sets the opacity of the label background.
 * {hexcolor} [options.borderColor] - Sets the color of the label background border.
 * {alpha} [options.borderAlpha] - Sets the opacity of the label background border.
 * {number} [options.borderThickness] - Sets the thickness of the label background border.
 * {number} [options.borderRadius] - Sets the radius for rounded label background.
 * {number} [options.padding] - The padding between extremities of the label and inner periphery
 * of the doughnut. For rectangular label background, it's relative to any of the 4 corners. While for
 * circular background, it's the gap between the 2 concentric circles, background border and inner
 * periphery.
 * {number} [options.textPadding] - For rectangular label background, it's the gutter between
 * the text and the background border. While for circular background, it's the minimum space between
 * the background border and the containing circle of the text.
 * {string} [options.toolText] - Sets the tooltext for the label.
 *
 * @fires FusionCharts#centerLabelChanged
 *
 * @example
 * // Render a doughnut 2d chart and set center label with some
 * // configuring params on click of a button
 * FusionCharts.ready(function () {
 *     var chart = new FusionCharts({
 *         type: "doughnut2d",
 *         renderAt: "chart-container",
 *         dataSource: "data.json",
 *         dataFormat: "jsonurl"
 *     }).render();
 *
 *     // Assign the functionality of setting the center label when clicked on
 *     // a button (with an id set-center-label).
 *     document.getElementById("set-center-label").onclick = function () {
 *         chart.centerLabel("The central label", {bold: true, toolText: "center label tooltext"});
 *     };
 * });
 */
function centerLabel (labelText, options) {
  var chart = this.apiInstance,
    dataset = chart.getChildren('dataset')[0],
    children = dataset.getChildren(),
    centerLabelComp = children.centerLabel && children.centerLabel[0],
    chartConfig = chart.config,
    _helperFn = function () {
      var seriesData = chart.getDatasets()[0],
        config = seriesData.config,
        piePlotOptions = config.piePlotOptions,
        innerSize = piePlotOptions.innerSize,
        centerLabelConfig,
        key;
      centerLabelConfig = centerLabelComp.config;

      if (typeof options !== 'object') {
        options = centerLabelConfig;
      } else {
        // Create the config cosmetics object from those obtained
        // from argument and default values
        for (key in centerLabelConfig) {
          options[key] === UNDEF && (options[key] = centerLabelConfig[key]);
        }
        centerLabelConfig = Object.assign(centerLabelConfig, options);
      }
      options.label = labelText;
      centerLabelConfig.label = labelText;
      seriesData.centerLabelConfig = centerLabelConfig;

      innerSize && centerLabelComp && centerLabelComp.draw(labelText || '');
    };
  if (centerLabelComp === UNDEF) {
    componentFactory(dataset, CenterLabelExtension, 'centerLabel', 1, [chartConfig]);
    centerLabelComp = children.centerLabel && children.centerLabel[0];
  }

  chart.addJob(`eiMethods-center-lebel${count++}`, _helperFn, priorityList.postRender);
}

export default {
  extension: function (FusionCharts) {
    var isApplicableChart;
    FusionCharts.addEventListener('instantiated', function (event) {
      let component = event.sender;
      !isApplicableChart &&
        (isApplicableChart = isChartAPI(component) && (isDoughnut2D(component) || isMultiLevelPie(component)));
      if (isApplicableChart && isDataset(component)) {
        component.registerFactory('centerLabel', function () {
          let chartConfig = component.getFromEnv('chartConfig'),
            chartInstance = component.getFromEnv('chartInstance'),
            hasCenterLabel = !!(chartConfig.centerlabel || chartConfig.defaultcenterlabel);
          chartInstance.centerLabel = centerLabel;
          componentFactory(component, CenterLabelExtension, 'centerLabel', (hasCenterLabel ? 1 : 0), [chartConfig]);
          isApplicableChart = UNDEF;
        });
      }
    });
  },
  name: 'CenterLabelExtension',
  type: 'extension',
  requiresFusionCharts: true
};
